import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import FilterBar from "@/components/feature/FilterBar";
import DramaCard, { DramaCardSkeleton } from "@/components/base/DramaCard";
import { Breadcrumbs, EmptyState } from "@/components/feature/PageShell";
import { dramas } from "@/mocks/dramas";
import { usePageMeta } from "@/hooks/usePageMeta";
import { defaultFilters, filterDramas, searchDramas, type FilterState } from "@/utils/catalog";

const suggestions = ["家庭", "昭和", "职场伦理", "爱情", "悬疑", "法庭", "当代都市"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setKeyword(query);
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [query]);

  const matched = useMemo(() => (query.trim() ? searchDramas(query) : []), [query]);
  const results = useMemo(() => filterDramas(matched, filters), [matched, filters]);

  usePageMeta({
    title: query ? `“${query}”的搜索结果 - 日本伦理剧` : "搜索 - 日本伦理剧",
    description: query
      ? `在日本伦理剧站内检索“${query}”相关的剧集、导演、演员与标签，共找到 ${matched.length} 条结果。`
      : "输入剧名、导演、演员或标签，站内检索日本伦理题材影视作品。",
    keywords: "日本伦理剧搜索,剧集检索,伦理题材影视",
  });

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {}, { replace: false });
    setFilters(defaultFilters);
  };

  const hotPicks = [...dramas].sort((a, b) => b.hot - a.hot).slice(0, 4);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200 bg-background-100/70">
          <div className="container-page py-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
            <h1 className="mt-5 font-heading text-[26px] font-semibold text-foreground-950 md:text-[36px]">
              {query ? (
                <>
                  “<span className="text-primary-800">{query}</span>” 的搜索结果
                </>
              ) : (
                "站内搜索"
              )}
            </h1>

            <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row" role="search">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 transition-colors focus-within:border-accent-500">
                <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-sm" />
                </span>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="剧名、导演、演员、标签，例如：家庭"
                  aria-label="搜索关键词"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                {keyword ? (
                  <button
                    type="button"
                    onClick={() => setKeyword("")}
                    aria-label="清空关键词"
                    className="flex h-5 w-5 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
                  >
                    <i className="ri-close-circle-fill text-sm" />
                  </button>
                ) : null}
              </div>
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-900 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800"
              >
                搜索
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-foreground-500">热门关键词</span>
              {suggestions.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setKeyword(item);
                    setSearchParams({ q: item });
                    setFilters(defaultFilters);
                  }}
                  className={`cursor-pointer rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                    query === item
                      ? "border-accent-400 bg-accent-100 text-accent-900"
                      : "border-background-300 text-foreground-600 hover:border-accent-400"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="container-page py-8 md:py-12">
          {query ? (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm text-foreground-600">
                  共找到 <span className="font-semibold text-foreground-950">{results.length}</span> 条结果
                  {matched.length !== results.length ? `（匹配 ${matched.length} 条，经筛选后 ${results.length} 条）` : ""}
                </p>
                <p className="text-[11px] text-foreground-400">模糊匹配：剧名 / 导演 / 演员 / 标签 / 年份</p>
              </div>

              <div className="mt-5">
                <FilterBar value={filters} onChange={setFilters} />
              </div>

              {loading ? (
                <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-5">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <DramaCardSkeleton key={index} />
                  ))}
                </div>
              ) : results.length === 0 ? (
                <div className="mt-6 grid gap-6 lg:grid-cols-12">
                  <div className="lg:col-span-7">
                    <EmptyState
                      title="没有找到匹配的作品"
                      desc="试试更短的关键词，或者换一个题材方向。也可以直接从下方热门内容进入。"
                      action={
                        <button
                          type="button"
                          onClick={() => {
                            setKeyword("");
                            setSearchParams({});
                            setFilters(defaultFilters);
                          }}
                          className="cursor-pointer rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 hover:border-accent-400"
                        >
                          清空搜索条件
                        </button>
                      }
                    />
                  </div>
                  <div className="lg:col-span-5">
                    <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
                      <p className="rule-label text-foreground-500">不妨看看热门内容</p>
                      <ul className="mt-3 divide-y divide-background-200">
                        {hotPicks.map((item) => (
                          <li key={item.id}>
                            <Link
                              to={`/detail/${item.id}`}
                              className="flex items-center justify-between gap-3 py-3 text-sm text-foreground-800 hover:text-accent-700"
                            >
                              <span className="line-clamp-1">
                                {item.title} <span className="text-[11px] text-foreground-400">/ {item.types[0]}</span>
                              </span>
                              <i className="ri-arrow-right-s-line text-foreground-400" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-5">
                  {results.map((drama) => (
                    <DramaCard key={drama.id} drama={drama} showSummary />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="grid gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <EmptyState
                  icon="ri-search-line"
                  title="输入关键词开始浏览"
                  desc="可以搜索剧名（如「静默的潮汐」）、导演、演员，也可以直接输入「家庭」「昭和」这类题材标签。"
                />
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
                  <p className="rule-label text-foreground-500">编辑较常被搜到的条目</p>
                  <ul className="mt-3 divide-y divide-background-200">
                    {hotPicks.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/detail/${item.id}`}
                          className="flex items-center justify-between gap-3 py-3 text-sm text-foreground-800 hover:text-accent-700"
                        >
                          <span className="line-clamp-1">{item.title}</span>
                          <span className="shrink-0 text-[11px] text-foreground-400">{item.year}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}